import * as Styles from './ResponsesTable.styles';

import {
	COLUMN_MAX_WIDTH,
	COLUMN_MIN_WIDTH,
	PAID_COLUMN_HEADINGS,
	REGISTRATION_COLUMN_HEADINGS,
	RESEND_EMAIL_COLUMN_MAX_WIDTH,
	RESEND_EMAIL_COLUMN_MIN_WIDTH,
	RESPONSES_INDEX,
} from './constants';
import { FC, useEffect, useState } from 'react';

import { FlexContainer } from 'features/common/components/FlexContainer';
import { OUTLINED } from 'features/common/components/Button/constants';
import { ResponsesProps, SortField } from 'features/CustomFieldsResponses/types';
import { Table } from 'features/common/components/Table';
import { TableBody } from './TableBody';
import { buildGridColumns, getCellIndex } from './utils';
import { useTranslation } from 'react-i18next';
import { SortableColumnHeader } from 'features/common/components/Table/SortableColumnHeader';
import { ScrollableTableWrapper } from 'features/common/components/Table/ScrollableTableWrapper';

export const ResponsesTable: FC<ResponsesProps> = ({
	responsesQueryObject,
	isAttendeesList,
	eventMode,
	sortBy,
	order,
	onSort,
}) => {
	const { t, i18n } = useTranslation();
	const { fetchNextPage, hasNextPage, data } = responsesQueryObject;
	const [headings, setHeadings] = useState<string[]>([]);
	const [questions, setQuestions] = useState<string[]>([]);
	const [showDownloadQR, setShowDownloadQR] = useState(false);
	const customHeadings = eventMode === 'normal' ? PAID_COLUMN_HEADINGS : REGISTRATION_COLUMN_HEADINGS;

	useEffect(() => {
		const tempHeadings = [...customHeadings];
		if (!data || data.pages.length === 0) {
			setShowDownloadQR(false);
			setHeadings(tempHeadings);
			return;
		}

		const questions: string[] = [];
		data.pages.forEach((page) => {
			page.forEach((response) => {
				response.responses.forEach((response) => {
					if (!['ATTENDEE_NAME', 'ATTENDEE_EMAIL'].includes(response.custom_field.name))
						questions.push(response.custom_field.name);
				});
			});
		});

		const uniqueQuestions = Array.from(new Set(questions));
		setQuestions(uniqueQuestions);

		if (isAttendeesList) {
			tempHeadings.push(...uniqueQuestions);
		} else {
			tempHeadings.splice(RESPONSES_INDEX, 0, ...uniqueQuestions);
		}
		setHeadings(tempHeadings);

		if (data.pages.some((page) => page.some((response) => response.can_download_qr))) {
			setShowDownloadQR(true);
		}
	}, [data?.pages]);

	const baseGridColumns = buildGridColumns(headings, customHeadings);
	const qrColumn = isAttendeesList && showDownloadQR ? ` minmax(${COLUMN_MIN_WIDTH[9]}px, 1fr)` : '';
	const resendEmailColumn = ` minmax(${RESEND_EMAIL_COLUMN_MIN_WIDTH}px, 1fr)`;
	const gridColumns = `${baseGridColumns}${qrColumn}${resendEmailColumn}`;

	// Mapping between heading translation keys and sort field names
	const getSortField = (heading: string): SortField | null => {
		const sortFieldMap: Record<string, SortField> = {
			'customFields-responses-table-orderId': 'order_id',
			'customFields-responses-table-email': 'email',
			'customFields-responses-table-fullName': 'full_name',
			'customFields-responses-table-amount': 'amount',
			'customFields-responses-table-date': 'date',
			'customFields-responses-table-status': 'status',
			'customFields-responses-table-scanned': 'scan_count',
		};
		return sortFieldMap[heading] || null;
	};

	return (
		<>
			<ScrollableTableWrapper>
				<Table.TableContainer>
					<Table>
						<thead>
							<Styles.HeaderRow gridColumns={gridColumns} textAlign="center">
								{headings.map((heading, i) => {
									const sortField = getSortField(heading);
									const cellIndex = getCellIndex(i, headings, RESPONSES_INDEX, customHeadings);

									return (
										<Table.Cell
											key={heading}
											minWidth={COLUMN_MIN_WIDTH[cellIndex]}
											maxWidth={COLUMN_MAX_WIDTH[cellIndex]}
										>
											{sortField ? (
												<SortableColumnHeader
													label={i18n.exists(heading) ? t(heading) : heading}
													sortKey={sortField}
													currentSortBy={sortBy}
													currentOrder={order}
													onSort={onSort}
													align="center"
												/>
											) : (
												<Table.ColumnHeader>{i18n.exists(heading) ? t(heading) : heading}</Table.ColumnHeader>
											)}
										</Table.Cell>
									);
								})}

								{isAttendeesList && showDownloadQR && (
									<Table.Cell minWidth={COLUMN_MIN_WIDTH[9]} maxWidth={COLUMN_MAX_WIDTH[9]} textAlign="center">
										<Table.ColumnHeader>{t('customFields-responses-ticketQR')}</Table.ColumnHeader>
									</Table.Cell>
								)}
								<Table.Cell
									minWidth={RESEND_EMAIL_COLUMN_MIN_WIDTH}
									maxWidth={RESEND_EMAIL_COLUMN_MAX_WIDTH}
									textAlign="center"
								>
									<Table.ColumnHeader>{t('customFields-responses-table-resendEmail')}</Table.ColumnHeader>
								</Table.Cell>
							</Styles.HeaderRow>
						</thead>
						<TableBody
							responsesQueryObject={responsesQueryObject}
							columnsN={headings.length}
							gridColumns={gridColumns}
							questions={questions}
							isAttendeesList={isAttendeesList}
							showDownloadQR={showDownloadQR}
							eventMode={eventMode}
						/>
					</Table>
				</Table.TableContainer>
			</ScrollableTableWrapper>
			<FlexContainer justifyContent="center" margin="32px 0">
				<Table.Button
					category={OUTLINED}
					text={t('button-load-more')}
					onClick={() => fetchNextPage()}
					disabled={!hasNextPage}
					type="button"
					width="auto"
				/>
			</FlexContainer>
		</>
	);
};
